import { components } from "@/api-schema";
import { TransportCompanyLoadCapacityExceededErrorCode } from "@/api-schema-enums";
import { isAxiosError } from "axios";

type TransportCompanyLoadCapacityExceededResponse =
  components["schemas"]["TransportCompanyLoadCapacityExceededResponseDto"];
type TranslateFunction = (key: string, options?: Record<string, unknown>) => string;

function getAxiosResponseData(error: unknown): unknown | null {
  if (!isAxiosError(error)) {
    return null;
  }

  return error.response?.data ?? null;
}

function isTransportCompanyLoadCapacityExceededResponse(
  value: unknown,
): value is TransportCompanyLoadCapacityExceededResponse {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<TransportCompanyLoadCapacityExceededResponse>;

  return (
    candidate.errorCode === TransportCompanyLoadCapacityExceededErrorCode.FitTransportCompanyLoadCapacityExceeded &&
    typeof candidate.message === "string" &&
    typeof candidate.statusCode === "number" &&
    !!candidate.details &&
    typeof candidate.details.maxVehicles === "number" &&
    typeof candidate.details.currentVehicles === "number"
  );
}

function extractTransportCompanyLoadCapacityExceededResponse(
  error: unknown,
): TransportCompanyLoadCapacityExceededResponse | null {
  const responseData = getAxiosResponseData(error);

  if (!isTransportCompanyLoadCapacityExceededResponse(responseData)) {
    return null;
  }

  return responseData;
}

function extractApiErrorMessage(error: unknown): string | null {
  const responseData = getAxiosResponseData(error);
  if (!responseData || typeof responseData !== "object" || !("message" in responseData)) {
    return null;
  }

  return typeof responseData.message === "string" ? responseData.message : null;
}

export function resolveLoadErrorDescription(error: unknown, fallbackMessage: string, t: TranslateFunction): string {
  if (!isAxiosError(error) || error.response?.status !== 409) {
    return fallbackMessage;
  }

  const apiErrorResponse = extractTransportCompanyLoadCapacityExceededResponse(error);
  if (
    apiErrorResponse?.errorCode ===
    TransportCompanyLoadCapacityExceededErrorCode.FitTransportCompanyLoadCapacityExceeded
  ) {
    return t("fit:MyTruckVehicleLimitReached", { count: apiErrorResponse.details.maxVehicles });
  }

  return extractApiErrorMessage(error) ?? fallbackMessage;
}
